import { debounce } from 'lodash-es';

export function tabpanel() {
  
  const ACTIVE_CLASS = 'active';
  const TAB_PANEL_SELECTOR = '.tab-panel--container';
  const DESKTOP_BREAKPOINT = 992;

  if (!document.querySelector(TAB_PANEL_SELECTOR)) return;

  // ----- Helpers -------------------------------------------------------------

  const isDesktop = () => window.innerWidth >= DESKTOP_BREAKPOINT;


  // Delay ARIA initialization to let the viewport settle on mobile
  requestAnimationFrame(() => {
    updateTablistRoles();

    const desktopMode = isDesktop();
    document.querySelectorAll('.tab-control').forEach(ctrl => {
      clearCtrlAria(ctrl, desktopMode);
    });
  });

  // Set ARIA for a control based on current mode and desired boolean value
  function setCtrlAria(ctrl, desktopMode, isActive) {
    // Remove the attribute that doesn't apply to this breakpoint
    if (desktopMode) {
      ctrl.removeAttribute('aria-expanded');
      ctrl.setAttribute('aria-selected', isActive ? 'true' : 'false');
      ctrl.setAttribute('role', 'tab');
    } else {
      ctrl.removeAttribute('aria-selected');
      ctrl.setAttribute('aria-expanded', isActive ? 'true' : 'false');
      ctrl.removeAttribute('role');
    }
  }

  // Clear ARIA for a control according to current breakpoint (sets relevant attr to "false")
  function clearCtrlAria(ctrl, desktopMode) {
    setCtrlAria(ctrl, desktopMode, false);
  }

  // Remove all states inside nested panels and normalize ARIA for current breakpoint
  function clearNestedStates(panelEl) {
    const desktopMode = isDesktop();
    panelEl.querySelectorAll('.tab-control.active').forEach(ctrl => {
      ctrl.classList.remove('active');
      clearCtrlAria(ctrl, desktopMode);
    });
    panelEl
      .querySelectorAll('.tab-panel--mobile.active, .tab-panel--desktop.active')
      .forEach(p => p.classList.remove('active'));
  }

  // ----- Core logic ----------------------------------------------------------

  function setActiveTab(tabPanel, key, desktopMode, allowToggle = true) {
    const controls = tabPanel.querySelectorAll('.tab-control');
    const mobilePanels = tabPanel.querySelectorAll('.tab-panel--mobile');
    const desktopPanels = Array.from(tabPanel.children).filter(el =>
      el.classList.contains('tab-panel--desktop')
    );

    const activeControl = [...controls].find(c => c.classList.contains(ACTIVE_CLASS));
    const activeKey = activeControl ? activeControl.getAttribute('data-tab-key') : null;
    const isSameKey = activeKey === key;

    if (allowToggle && isSameKey) {
      deactivateKey(tabPanel, key, controls, mobilePanels, desktopPanels, true);
      return;
    }

    // Reset all controls/panels first
    controls.forEach(ctrl => {
      ctrl.classList.remove(ACTIVE_CLASS);
      clearCtrlAria(ctrl, desktopMode); // sets relevant attr to "false" and removes the other
    });

    mobilePanels.forEach(p => p.classList.remove(ACTIVE_CLASS));
    desktopPanels.forEach(p => {
      p.classList.remove(ACTIVE_CLASS);
      clearNestedStates(p);
    });

    // Activate the chosen control/panels
    controls.forEach(ctrl => {
      if (ctrl.getAttribute('data-tab-key') === key) {
        ctrl.classList.add(ACTIVE_CLASS);
        setCtrlAria(ctrl, desktopMode, true); // sets relevant attr to "true" and removes the other
      }
    });

    mobilePanels.forEach(panel => {
      if (panel.getAttribute('data-tab-key') === key) panel.classList.add(ACTIVE_CLASS);
    });
    desktopPanels.forEach(panel => {
      if (panel.getAttribute('data-tab-key') === key) panel.classList.add(ACTIVE_CLASS);
    });
  }
  
  function updateTablistRoles() {
    const desktopMode = isDesktop();
    document.querySelectorAll('.tab-controls').forEach(ctrlContainer => {
      if (desktopMode) {
        ctrlContainer.setAttribute('role', 'tablist');
      } else {
        ctrlContainer.removeAttribute('role');
      }
    });
  }

  // Run once on load
  updateTablistRoles();

  function deactivateKey(tabPanel, key, controls, mobilePanels, desktopPanels, clearChildren = false) {
    const desktopMode = isDesktop();
    controls.forEach(ctrl => {
      if (ctrl.getAttribute('data-tab-key') === key) {
        ctrl.classList.remove(ACTIVE_CLASS);
        clearCtrlAria(ctrl, desktopMode); // relevant attr -> "false", other removed
      }
    });



    mobilePanels.forEach(panel => {
      if (panel.getAttribute('data-tab-key') === key) panel.classList.remove(ACTIVE_CLASS);
    });

    desktopPanels.forEach(panel => {
      if (panel.getAttribute('data-tab-key') === key) {
        panel.classList.remove(ACTIVE_CLASS);
        if (clearChildren) clearNestedStates(panel);
      }
    });
  }

  function openParentChains(childPanel) {
    const chain = [];
    let parentPanelEl = childPanel.closest('.tab-panel--mobile, .tab-panel--desktop');
    while (parentPanelEl) {
      const parentContainer = parentPanelEl.closest('.tab-panel--container');
      if (!parentContainer) break;
      const parentKey = parentPanelEl.getAttribute('data-tab-key');
      if (parentKey) {
        chain.unshift({ container: parentContainer, key: parentKey });
      }
      parentPanelEl = parentContainer.closest('.tab-panel--mobile, .tab-panel--desktop');
    }
    // Activate outermost → innermost so clearNestedStates never wipes a child we just opened
    chain.forEach(({ container, key }) => {
      setActiveTab(container, key, isDesktop(), false);
    });
  }

  // ----- Init per container --------------------------------------------------

  document.querySelectorAll(TAB_PANEL_SELECTOR).forEach(tabPanel => {
    const id = tabPanel.id;
    const controls = Array.from(
      tabPanel.querySelectorAll(':scope > .tab-controls--container .tab-control')
    );
    const desktopMode = isDesktop();

    const url = new URL(window.location.href);
    const idFromUrl = url.searchParams.get('idTab');
    const activeIndexFromUrl = url.searchParams.get('activeTab');

    // Deep-linking handling (active tab via URL)
    if (id && id === idFromUrl && activeIndexFromUrl !== null) {
      // Large tabs render content in both a mobile-only and a desktop-only panel —
      // the same nested container ID appears twice in the DOM. Only process the visible copy.
      const desktopNow = isDesktop();
      const insideMobileOnly = tabPanel.closest('.tab-panel--mobile:not(.tab-panel--desktop)');
      const insideDesktopOnly = tabPanel.closest('.tab-panel--desktop:not(.tab-panel--mobile)');
      const isDuplicate = (desktopNow && insideMobileOnly) || (!desktopNow && insideDesktopOnly);

      if (!isDuplicate) {
        const idx = parseInt(activeIndexFromUrl, 10);
        if (!isNaN(idx) && controls[idx]) {
          // Stop the browser from restoring scroll on reload and from retrying
          // hash navigation after our JS scroll makes the element visible.
          if ('scrollRestoration' in history) history.scrollRestoration = 'manual';
          // Strip the hash synchronously so Chrome does not re-jump to it
          // after we finish scrolling (Chrome retries hashes for hidden elements).
          history.replaceState(null, '', window.location.pathname + window.location.search);

          const key = controls[idx].getAttribute('data-tab-key');
          openParentChains(tabPanel);
          setActiveTab(tabPanel, key, desktopMode, false);

          setTimeout(() => {
            // scrollIntoView is unreliable for elements made visible just now —
            // use window.scrollTo with getBoundingClientRect for a guaranteed window scroll.
            // Scroll to the container itself, not the inner active panel — the
            // panel sits below .tab-controls--container, so targeting it directly
            // scrolls the tab-controls above the fold, out of view.
            const rect = tabPanel.getBoundingClientRect();
            window.scrollTo({ top: rect.top + window.scrollY, behavior: 'smooth' });

            // Re-add the hash only once the smooth scroll has actually finished —
            // doing it immediately (before the animation completes) makes Chrome
            // retry its own hash navigation mid-animation, fighting our scroll
            // and snapping the page back toward the top.
            const reAddHash = () => {
              history.replaceState(
                null,
                '',
                window.location.pathname + window.location.search + '#' + id
              );
            };
            if ('onscrollend' in window) {
              window.addEventListener('scrollend', reAddHash, { once: true });
            } else {
              setTimeout(reAddHash, 1000);
            }
          }, 300);
        }
      }
    }

    // Click handling
    controls.forEach((tab, index) => {
      tab.addEventListener(
        'click',
        event => {
          // Skip tab logic if tab-control is a link (<a>)
          if (tab.tagName.toLowerCase() === 'a') return;

          event.preventDefault();
          event.stopImmediatePropagation();

          const currentContainer = tab.closest(TAB_PANEL_SELECTOR);
          if (!currentContainer) return;

          const key = tab.getAttribute('data-tab-key');
          const desktopNow = isDesktop();

          setActiveTab(currentContainer, key, desktopNow);

          const id = currentContainer.id;
          if (id) {
            const newUrl = new URL(window.location.href);
            const isActive = tab.classList.contains(ACTIVE_CLASS);
            if (isActive) {
              newUrl.hash = id;
              newUrl.searchParams.set('idTab', id);
              newUrl.searchParams.set('activeTab', index);
            } else {
              // Don't clear the hash — it may belong to other page functionality
              newUrl.searchParams.delete('idTab');
              newUrl.searchParams.delete('activeTab');
            }
            window.history.replaceState(null, '', newUrl.toString());
          }
        },
        true // capture phase
      );
    });
  });

  // ----- Resize handling -----------------------------------------------------

  let windowSize = window.innerWidth;
  window.addEventListener(
    'resize',
    debounce(() => {
      const desktopNow = isDesktop();
      // Only react when crossing the breakpoint
      if ((desktopNow && windowSize < DESKTOP_BREAKPOINT) || (!desktopNow && windowSize >= DESKTOP_BREAKPOINT)) {
        document.querySelectorAll(TAB_PANEL_SELECTOR).forEach(tabPanel => {
          const activeControl = tabPanel.querySelector('.tab-control.active');
          if (activeControl) {
            const key = activeControl.getAttribute('data-tab-key');
            // Re-apply to flip ARIA correctly at the new breakpoint
            setActiveTab(tabPanel, key, desktopNow, false);
          } else {
            // Normalize inactive controls so they have the correct relevant attr="false"
            tabPanel.querySelectorAll('.tab-control').forEach(ctrl => clearCtrlAria(ctrl, desktopNow));
          }
        });
        windowSize = window.innerWidth;
      }
      document.querySelectorAll('.tab-controls').forEach(ctrlContainer => {
      if (desktopNow) {
        ctrlContainer.setAttribute('role', 'tablist');
      } else {
        ctrlContainer.removeAttribute('role');
      }
    });
    }, 200)
  );
}

export default tabpanel;